/**
 * Utility functions for OKLCH color space calculations
 */

export interface OklchColor {
  l: number; // Lightness (0-1)
  c: number; // Chroma (0-0.4)
  h: number; // Hue (0-360)
}

/**
 * Parse an OKLCH color string to components
 */
export function parseOklch(oklchString: string): OklchColor {
  const match = oklchString.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/);
  if (!match) {
    return { l: 0.65, c: 0.2, h: 180 }; // Default fallback
  }
  return {
    l: parseFloat(match[1]),
    c: parseFloat(match[2]),
    h: parseFloat(match[3])
  };
}

/**
 * Format OKLCH components to a CSS string
 */
export function formatOklch(color: OklchColor): string {
  return `oklch(${color.l} ${color.c} ${color.h})`;
}

/**
 * Format OKLCH for display with rounded values (2 decimal places)
 */
export function formatOklchForDisplay(oklchString: string): string {
  const color = parseOklch(oklchString);
  return `oklch(${color.l.toFixed(2)} ${color.c.toFixed(2)} ${color.h.toFixed(2)})`;
}

/**
 * Calculate the angular distance between two hues (0-360)
 * Returns the smallest angle between them
 */
function hueDistance(h1: number, h2: number): number {
  const diff = Math.abs(h1 - h2);
  return Math.min(diff, 360 - diff);
}

/**
 * Find the largest gap in hue space and return the hue that splits it
 * This ensures maximum visual distinction between colors
 */
export function calculateOptimalNextHue(existingColors: string[]): number {
  if (existingColors.length === 0) {
    // First color: use a pleasant starting hue (blue)
    return 220;
  }

  // Parse all existing hues
  const hues = existingColors
    .map(parseOklch)
    .map(c => c.h)
    .sort((a, b) => a - b);

  if (hues.length === 1) {
    // Second color: opposite side of color wheel
    return (hues[0] + 180) % 360;
  }

  // Find the largest gap between consecutive hues
  let maxGap = 0;
  let maxGapStart = 0;

  for (let i = 0; i < hues.length; i++) {
    const currentHue = hues[i];
    const nextHue = hues[(i + 1) % hues.length];
    
    // Calculate gap considering circular nature of hue
    let gap: number;
    if (i === hues.length - 1) {
      // Gap between last and first (wrapping around)
      gap = (360 - currentHue) + hues[0];
    } else {
      gap = nextHue - currentHue;
    }

    if (gap > maxGap) {
      maxGap = gap;
      maxGapStart = currentHue;
    }
  }

  // Return the midpoint of the largest gap
  const newHue = (maxGapStart + maxGap / 2) % 360;
  return Math.round(newHue);
}

/**
 * Generate an optimal OKLCH color based on existing colors
 * Uses consistent lightness and chroma for visual harmony
 */
export function generateOptimalOklchColor(existingColors: string[]): string {
  const hue = calculateOptimalNextHue(existingColors);
  
  // Use consistent lightness and chroma for all board colors
  // L: 0.65 = medium-light (good visibility on both light and dark)
  // C: 0.2 = moderately saturated (vibrant but not overwhelming)
  return formatOklch({ l: 0.65, c: 0.2, h: hue });
}

/**
 * Convert OKLCH to hex for compatibility with color pickers
 */
export function oklchToHex(oklchString: string): string {
  const { l, c, h } = parseOklch(oklchString);
  
  // Simplified conversion (approximate)
  // For a proper conversion, you'd need a full color space transformation
  // This is a rough approximation that works for most cases
  const hueRadians = (h * Math.PI) / 180;
  const a = c * Math.cos(hueRadians);
  const b = c * Math.sin(hueRadians);
  
  // Convert Lab to XYZ to RGB (simplified)
  const y = (l + 0.16) / 1.16;
  const x = a / 5 + y;
  const z = y - b / 2;
  
  // XYZ to RGB
  let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
  let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
  let _b = x * 0.0557 + y * -0.2040 + z * 1.0570;
  
  // Gamma correction
  r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
  g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
  _b = _b > 0.0031308 ? 1.055 * Math.pow(_b, 1 / 2.4) - 0.055 : 12.92 * _b;
  
  // Clamp and convert to hex
  r = Math.max(0, Math.min(1, r));
  g = Math.max(0, Math.min(1, g));
  _b = Math.max(0, Math.min(1, _b));
  
  const toHex = (n: number) => {
    const hex = Math.round(n * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(_b)}`;
}

/**
 * Convert hex to OKLCH (approximate)
 */
export function hexToOklch(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // RGB to XYZ (simplified)
  const rLinear = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  const gLinear = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  const bLinear = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
  
  const x = rLinear * 0.4124 + gLinear * 0.3576 + bLinear * 0.1805;
  const y = rLinear * 0.2126 + gLinear * 0.7152 + bLinear * 0.0722;
  const z = rLinear * 0.0193 + gLinear * 0.1192 + bLinear * 0.9505;
  
  // XYZ to Lab to LCH (approximate)
  const l = 1.16 * y - 0.16;
  const a = 5 * (x - y);
  const _b = 2 * (y - z);
  
  const c = Math.sqrt(a * a + _b * _b);
  let h = (Math.atan2(_b, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  
  return formatOklch({ l, c, h });
}
